'use client';
import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react';
import { Equipment } from 'services/equipment/types';
import {
    searchPagination
} from 'services/equipment/utils';

interface MyEquipmentContextProviderTypes {
    selectedEquipmentIndex: number;
    myEquipmentList: Equipment[] | null;
}

interface UseMyEquipmentContextProps {
    onSearch: (input: string) => void;
    clearSelection: () => void;
    searchValue: string;
    paginatedEquipmentList: Equipment[];
    totalPageCount: number;
    page: string;
    setPage: Dispatch<SetStateAction<string>>;
    selectedEquipmentIndex: number;
}

const MyEquipmentContext = createContext<UseMyEquipmentContextProps>(null);

export const useMyEquipmentContext = () => useContext(MyEquipmentContext);

export const MyEquipmentContextProvider: FC<
    PropsWithChildren<MyEquipmentContextProviderTypes>
> = ({ children, myEquipmentList, selectedEquipmentIndex }) => {
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState('1');

    const onSearch = useCallback((input: string) => {
        setPage('1');
        setSearchValue(input);
    }, []);

    const clearSelection = useCallback(() => {
        Number(page) > 1 && setPage('1');
        searchValue && setSearchValue('');
    }, [page, searchValue]);


    const { paginatedEquipmentList, totalPageCount } = useMemo(() => {
        return searchPagination(myEquipmentList,
            selectedEquipmentIndex,
            searchValue, Number(page) || 1);
    }, [myEquipmentList, page, searchValue, selectedEquipmentIndex]);

    const myEquipmentContextValue = useMemo(() => {
        return {
            onSearch,
            searchValue,
            paginatedEquipmentList,
            totalPageCount,
            page,
            setPage,
            clearSelection,
            selectedEquipmentIndex
        };
    }, [
        clearSelection,
        onSearch,
        page,
        paginatedEquipmentList,
        searchValue,
        totalPageCount,
        selectedEquipmentIndex
    ]);

    return (
        <MyEquipmentContext.Provider value={myEquipmentContextValue}>
            {children}
        </MyEquipmentContext.Provider>
    );
};